// markup/scaleCheck.mjs — does the scale being typed make sense? (pure)
// ---------------------------------------------------------------------------
// Brett 2026-09-15: "I did a calibrate and m2 is way wrong." He drew the line
// over the plan's 2090 dimension and typed 2090 with the unit sitting on
// metres, so the line was taken as 2 km and every area came out a million times
// too big (29,939,475 m²).
//
// A number alone cannot say it is wrong; the SHEET can. Scale the whole drawing
// by what was typed and ask how wide the paper would be in the real world. A
// 1:1 detail on A4 is ~0.3 m across; a 1:2000 site plan on A0 is ~2.4 km.
// Outside that band the unit is almost certainly the mistake, and the unit that
// brings the sheet back inside it is the fix to offer.
// ---------------------------------------------------------------------------

export const MM_PER = { mm: 1, cm: 10, m: 1000, in: 25.4, ft: 304.8 };
export const SHEET_MIN_MM = 200;
export const SHEET_MAX_MM = 5000000;
// The unit to offer, in the order drawings are written: construction plans are
// dimensioned in millimetres, then metres, centimetres almost never. The first
// that puts the sheet back in range wins. (Scoring against a "typical" sheet
// width offered cm on a small plan, where mm and cm were both in range.)
const PREFER = { metric: ['mm', 'm', 'cm'], imperial: ['in', 'ft'] };

// How wide the drawing is in real millimetres if the reference line is knownMm.
export function sheetAcrossMm(knownMm, refA, refB, W, H) {
  if (!(knownMm > 0) || !refA || !refB || !(W > 0) || !(H > 0)) return null;
  const d = Math.hypot((refB.x - refA.x) * W, (refB.y - refA.y) * H);
  if (!(d > 0)) return null;
  return (W * knownMm) / d;
}

export function plausibleSheet(mm) {
  return mm != null && mm >= SHEET_MIN_MM && mm <= SHEET_MAX_MM;
}

// { acrossMm, plausible, suggestUnit } for `value` typed in `unit`. The
// suggestion stays in the same family (metric or imperial).
export function checkScale(value, unit, refA, refB, W, H) {
  const v = Number(value);
  const per = MM_PER[unit];
  const acrossMm = (v > 0 && per) ? sheetAcrossMm(v * per, refA, refB, W, H) : null;
  if (acrossMm == null) return { acrossMm: null, plausible: true, suggestUnit: null };
  const plausible = plausibleSheet(acrossMm);
  let suggestUnit = null;
  if (!plausible) {
    const family = (unit === 'ft' || unit === 'in') ? PREFER.imperial : PREFER.metric;
    suggestUnit = family.find((u) => u !== unit && plausibleSheet(acrossMm * (MM_PER[u] / per))) || null;
  }
  return { acrossMm, plausible, suggestUnit };
}

// A scale already saved on a drawing that fails the same check.
export function calibrationLooksWrong(calibration, W, H) {
  if (!calibration) return false;
  const mm = sheetAcrossMm(calibration.knownMm, calibration.refA, calibration.refB, W, H);
  return mm != null && !plausibleSheet(mm);
}

export function formatAcross(mm, imperial = false) {
  if (!(mm > 0)) return '';
  if (imperial) {
    const ft = mm / 304.8;
    if (ft >= 10560) return `${Math.round(ft / 5280)} mi`;
    return `${ft >= 10 ? Math.round(ft) : ft.toFixed(1)} ft`;
  }
  if (mm >= 1e6) {
    const km = mm / 1e6;
    return `${km >= 10 ? Math.round(km) : km.toFixed(1)} km`;
  }
  const m = mm / 1000;
  return `${m >= 10 ? Math.round(m) : m.toFixed(m >= 1 ? 1 : 2)} m`;
}
